import api from './chatAxiosConfig';
import request from '../../api/request';

const chatApi = {
  askAi: async (message, context = null) => {
    const res = await request({
      url: '/ai-assistant/chat',
      method: 'POST',
      data: { message, context },
    });
    return res;
  },

  getMessages: async (chatRoomId, { page = 1, limit = 10, search = '' } = {}) => {
    const res = await api.get(`/messages/${chatRoomId}`, {
      params: { page, limit, search },
    });
    return res.data?.data || [];
  },

  // The `/list` endpoint is paginated on the server (default 50 rows per page,
  // newest conversation first). Requesting it without any pagination param used to
  // return only the first page, so every older conversation - and every room that
  // has no message yet - silently disappeared from the drawer. Walk through the
  // pages until the server returns a short page so the caller always gets the
  // complete chat list.
  // `archived` is tri-state: leave it out for every chat (archived included),
  // pass false for the normal list, pass true for the archived section.
  getChatListApiTrigger: async ({ limit = 100, maxPages = 50, archived = null } = {}) => {
    const allChats = [];
    const seenRoomIds = new Set();

    for (let page = 1; page <= maxPages; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await api.get('/list', {
        params: { page, limit, ...(archived === null ? {} : { archived }) },
      });

      const rows = res.data?.data || [];

      rows.forEach((chat) => {
        if (!seenRoomIds.has(chat.chat_room_id)) {
          seenRoomIds.add(chat.chat_room_id);
          allChats.push(chat);
        }
      });

      if (rows.length < limit) break;
    }

    return allChats;
  },

  // Archive / unarchive is per user - it only hides the conversation from the
  // caller's own list, everybody else keeps seeing it where it was.
  setChatArchived: async (chatRoomId, isArchived) => {
    const res = await api.post(isArchived ? '/archive' : '/unarchive', {
      chatRoomId,
    });
    return res?.data;
  },

  createOrGetChatRoom: async (userID) => {
    const res = await api.post('/create-or-get', {
      otherUserId: userID,
    });
    return res?.data;
  },

  getAllEmployeesList: async ({ page = 1, limit = 50, search = '' }) => {
    const res = await api.get('/get-all-employees', {
      params: {
        page,
        limit,
        search,
      },
    });

    return res.data || {};
  },

  removeMemberFromGroup: async (chatRoomId, memberId) => {
    const res = await api.post('/group/remove-member', {
      chatRoomId,
      memberId,
    });
    return res?.data;
  },

  leaveGroup: async (chatRoomId) => {
    const res = await api.post('/group/leave', {
      chatRoomId,
    });
    return res?.data;
  },

  deleteGroup: async (chatRoomId) => {
    const res = await api.delete('/group/delete', {
      data: { chatRoomId },
    });
    return res?.data;
  },

  createGroup: async (groupName, memberIds, rowDetails) => {
    const res = await api.post('/group/create', {
      groupName,
      memberIds,
      reference_id: rowDetails?.expense_id,
      description: rowDetails?.description,
      profile_pic: rowDetails?.profile_pic,
    });
    return res?.data;
  },

  addMemberToGroup: async (chatRoomId, memberIds) => {
    const res = await api.post('/group/add-members', {
      chatRoomId,
      memberIds,
    });
    return res?.data;
  },

  updateGroup: async (rowDetails) => {
    const res = await api.put('/group/update', {
      chatRoomId: rowDetails?.chatRoomId,
      name: rowDetails?.groupName,
      photo: rowDetails?.profile_pic,
      description: rowDetails?.description,
    });
    return res?.data;
  },

  getGroupInfo: async (chatRoomId) => {
    const res = await api.get(`/group/get-data?chatRoomId=${chatRoomId}`);
    return res?.data;
  },
};

export default chatApi;
